import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SimpleBar from "simplebar-react";
import MetisMenu from "metismenujs";
import withRouter from "../../components/Common/withRouter";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";

const Sidebar = (props) => {
  const ref = useRef();
  const menuRef = useRef(null);
  const [filteredSidebarData, setFilteredSidebarData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(Date.now()); // Force re-render on hot-reload

  const SidebarData = [
    {
      label: "Menu",
      isMainMenu: true,
    },
    {
      label: "Dashboard",
      icon: "mdi mdi-home-variant-outline",
      url: "/dashboard",
      issubMenubadge: true,
      bgcolor: "bg-primary",
      badgeValue: "3",
    },
    {
      label: "Components",
      isMainMenu: true,
    },
    {
      label: "Authorization",
      icon: "ri-eraser-fill",
      url: "/Role",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Patient",
      icon: "mdi mdi-account-heart",
      url: "/Patient",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Settings",
      isMainMenu: true,
    },
    {
      label: "User",
      icon: "mdi mdi-account-circle-outline",
      url: "/User",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Insurance",
      icon: "mdi mdi-shield-check-outline",
      url: "/Insurance",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Provider",
      icon: "mdi mdi-doctor",
      url: "/Provider",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Practice",
      icon: "mdi mdi-stethoscope",
      url: "/Practice",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Facilities",
      icon: "mdi mdi-office-building-outline",
      url: "/Facilities",
      issubMenubadge: true,
      bgcolor: "bg-primary",
    },
    {
      label: "Codes",
      icon: "mdi mdi-folder",
      issubMenubadge: true,
      bgcolor: "bg-primary",
      isHasArrow: true,
      subItem: [
        {
          sublabel: "Diagnosis",
          link: "/Diagnosis",
        },
        {
          sublabel: "Procedure",
          link: "/Procedure",
        },
      ],
    },
  ];

  const permissionMap = {
    Dashboard: "Dashboard",
    User: "User",
    Practice: "Practice",
    Insurance: "Insurance",
    Provider: "Provider",
    Diagnosis: "Diagnosis",
    Authorization: "Authorization",
    Patient: "Patient",
    Facilities: "Facilities",
    Procedure: "Procedure",
    Codes: "Codes",
  };

  const permissions = useSelector((state) => state.permissions?.permissions || {});
  const permissionsError = useSelector((state) => state.permissions?.error);
  const providers = useSelector((state) => state.providers?.providers || []);

  useEffect(() => {
    console.log("Permissions from Redux:", permissions);
    console.log("Providers from:", providers);

    let storedPermissions = {};
    if (Object.keys(permissions).length === 0 && !permissionsError) {
      const stored = localStorage.getItem("permissionStored");
      storedPermissions = stored ? JSON.parse(stored) : {};
    }

    const effectivePermissions = Object.keys(permissions).length > 0 ? permissions : storedPermissions;
    const loading = Object.keys(effectivePermissions).length === 0 && !permissionsError;
    setIsLoading(loading);

    if (!loading && !permissionsError) {
      const filterItems = (items) => {
        return items
          .map((item) => {
            if (item.isMainMenu) {
              return item;
            }
            if (item.subItem) {
              const filteredSubItems = item.subItem
                .map((subItem) => {
                  const permissionKey = permissionMap[subItem.sublabel];
                  console.log(
                    `Checking sub-item: ${subItem.sublabel}, Permission Key: ${permissionKey}, Value: ${effectivePermissions[permissionKey]}`
                  );
                  return effectivePermissions[permissionKey] === true ? subItem : null;
                })
                .filter((subItem) => subItem !== null);
              if (filteredSubItems.length > 0) {
                return { ...item, subItem: filteredSubItems };
              }
              return null;
            }
            const permissionKey = permissionMap[item.label];
            console.log(
              `Checking item: ${item.label}, Permission Key: ${permissionKey}, Value: ${effectivePermissions[permissionKey]}`
            );
            if (!permissionKey) {
              return null;
            }
            return effectivePermissions[permissionKey] === true ? item : null;
          })
          .filter((item) => item !== null);
      };

      const sections = [];
      let currentSection = [];
      SidebarData.forEach((item) => {
        if (item.isMainMenu) {
          if (currentSection.length > 0) {
            sections.push(currentSection);
          }
          currentSection = [item];
        } else {
          currentSection.push(item);
        }
      });
      if (currentSection.length > 0) {
        sections.push(currentSection);
      }

      const filteredData = sections
        .map((section) => {
          const mainMenuItem = section[0];
          const sectionItems = section.slice(1);
          const filteredSectionItems = filterItems(sectionItems);
          return filteredSectionItems.length > 0
            ? [mainMenuItem, ...filteredSectionItems]
            : [];
        })
        .flat();

      console.log("Final Filtered Sidebar Data:", filteredData);
      setFilteredSidebarData(filteredData);
    } else if (permissionsError) {
      console.error("Permissions fetch error:", permissionsError);
      setFilteredSidebarData([]);
    }
  }, [permissions, permissionsError]);

  const activateParentDropdown = useCallback(
    (item) => {
      item.classList.add("active");
      const parent = item.parentElement;
      const parent2El = parent.childNodes[1];
      if (parent2El && parent2El.id !== "side-menu") {
        parent2El.classList.add("mm-show");
      }
      if (parent) {
        parent.classList.add("mm-active");
        const parent2 = parent.parentElement;
        if (parent2) {
          parent2.classList.add("mm-show");
          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.add("mm-active");
            parent3.childNodes[0].classList.add("mm-active");
            const parent4 = parent3.parentElement;
            if (parent4) {
              parent4.classList.add("mm-show");
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.add("mm-show");
                parent5.childNodes[0].classList.add("mm-active");
              }
            }
          }
        }
      }
      scrollElement(item);
      return false;
    },
    []
  );

  const removeActivation = (items) => {
    for (let i = 0; i < items.length; ++i) {
      const item = items[i];
      const parent = items[i].parentElement;
      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.length && parent.childNodes[1]
            ? parent.childNodes[1]
            : null;
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }
        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;
        if (parent2) {
          parent2.classList.remove("mm-show");
          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active");
            parent3.childNodes[0].classList.remove("mm-active");
            const parent4 = parent3.parentElement;
            if (parent4) {
              parent4.classList.remove("mm-show");
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show");
                parent5.childNodes[0].classList.remove("mm-active");
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const pathName = props.router.location.pathname;
    const fullPath = pathName;
    let matchingMenuItem = null;
    const ul = document.getElementById("side-menu-item");
    if (ul) {
      const items = ul.getElementsByTagName("a");
      removeActivation(items);
      for (let i = 0; i < items.length; ++i) {
        if (fullPath === items[i].pathname) {
          matchingMenuItem = items[i];
          break;
        }
      }
      if (matchingMenuItem) {
        activateParentDropdown(matchingMenuItem);
      }
    }
  }, [props.router.location.pathname, activateParentDropdown]);

  useEffect(() => {
    if (!isLoading && filteredSidebarData.length > 0) {
      // Clean up previous MetisMenu instance
      if (menuRef.current) {
        console.log("Disposing previous MetisMenu instance");
        menuRef.current.dispose();
        menuRef.current = null;
      }
      // Initialize new MetisMenu instance
      const menuElement = document.getElementById("side-menu-item");
      if (menuElement) {
        menuRef.current = new MetisMenu("#side-menu-item");
        console.log("MetisMenu initialized");
        activeMenu();
      } else {
        console.error("Menu element #side-menu-item not found");
      }
    }
    // Cleanup on unmount or dependency change
    return () => {
      if (menuRef.current) {
        console.log("Cleaning up MetisMenu on unmount");
        menuRef.current.dispose();
        menuRef.current = null;
      }
    };
  }, [activeMenu, isLoading, filteredSidebarData, reloadKey]);

  useEffect(() => {
    if (!isLoading && filteredSidebarData.length > 0 && ref.current) {
      console.log("Recalculating SimpleBar");
      ref.current.recalculate();
    }
  }, [isLoading, filteredSidebarData, reloadKey]);

  // Force re-render on hot-reload
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const handleHotReload = () => {
        console.log("Hot-reload detected, forcing sidebar refresh");
        setReloadKey(Date.now());
      };
      // Listen for module.hot changes (Webpack/Vite)
      if (module.hot) {
        module.hot.accept(() => handleHotReload());
      }
      return () => {
        if (module.hot) {
          module.hot.dispose(() => console.log("Hot module disposed"));
        }
      };
    }
  }, []);

  function scrollElement(item) {
    if (item && ref.current) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "#2B7B7E" }} />
      </Box>
    );
  }

  if (filteredSidebarData.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No menu items available</Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <div className="vertical-menu">
        <SimpleBar className="h-100" ref={ref}>
          <div id="sidebar-menu">
            <ul className="metismenu list-unstyled" id="side-menu-item">
              {(filteredSidebarData || []).map((item, key) => (
                <React.Fragment key={key}>
                  {item.isMainMenu ? (
                    <li className="menu-title">{props.t(item.label)}</li>
                  ) : (
                    <li key={key}>
                      <Link
                        to={item.url ? item.url : "/#"}
                        className={item.isHasArrow ? "has-arrow" : ""}
                      >
                        <i className={item.icon} style={{ marginRight: "5px" }}></i>
                        {item.issubMenubadge && (
                          <span
                            className={"badge rounded-pill float-end " + item.bgcolor}
                          >
                            {item.badgeValue || ""}
                          </span>
                        )}
                        <span>{props.t(item.label)}</span>
                      </Link>
                      {item.subItem && (
                        <ul className="sub-menu">
                          {item.subItem.map((subItem, subKey) => (
                            <li key={subKey}>
                              <Link
                                to={subItem.link}
                                className={
                                  subItem.subMenu ? "has-arrow waves-effect" : ""
                                }
                              >
                                {subItem.issubMenubadge && (
                                  <span
                                    className={
                                      "badge rounded-pill float-end " + subItem.bgcolor
                                    }
                                  >
                                    {subItem.badgeValue || ""}
                                  </span>
                                )}
                                <span>{props.t(subItem.sublabel)}</span>
                              </Link>
                              {subItem.subMenu && (
                                <ul className="sub-menu">
                                  {subItem.subMenu.map((menuItem, menuKey) => (
                                    <li key={menuKey}>
                                      <Link to="#">{props.t(menuItem.title)}</Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
          </div>
        </SimpleBar>
      </div>
    </React.Fragment>
  );
};

Sidebar.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(Sidebar));